import { useState, useEffect, useContext } from "react";
import Context from "../../storage/context";
import axios from "axios";
import "./UsersEditor.css";
import useAdminAuth from "../../hooks/useAdminAuth/useAdminAuth";

const UsersEditor = () => {
    const ctx = useContext(Context);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resetId, setResetId] = useState(null);
    const [resetPassword, setResetPassword] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [newUser, setNewUser] = useState({ email: "", password: "", role: "user" });
    const [showNewUser, setShowNewUser] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    let [protect, setProtect] = useState(false);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useAdminAuth(ctx.api_url, setProtect);

    function validate(email, password) {
        const errors = {};
        if (!email) errors.email = "נדרשת כתובת אימייל";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "כתובת אימייל לא תקינה";
        if (!password) errors.password = "נדרשת סיסמה";
        else if (password.length < 6) errors.password = "הסיסמה חייבת להכיל לפחות 6 תווים";
        return errors;
    }
    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    };

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${ctx.api_url}/users`, { headers });
            setUsers(res.data);
        } catch (err) {
            showMessage("שגיאה בטעינת משתמשים", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${ctx.api_url}/users/${id}`, { headers });
            setUsers(users.filter(u => u.id !== id));
            setDeleteConfirm(null);
            showMessage("משתמש נמחק בהצלחה");
        } catch (err) {
            showMessage("שגיאה במחיקה", "error");
        }
    };

    const handleResetPassword = async (id) => {
        if (!resetPassword) return;
        try {
            const user = users.find(u => u.id === id);
            await axios.put(`${ctx.api_url}/users/${id}`, {
                email: user.email,
                password: resetPassword,
                type: user.type
            }, { headers });
            setResetId(null);
            setResetPassword("");
            showMessage("סיסמא עודכנה בהצלחה");
        } catch (err) {
            showMessage("שגיאה בעדכון סיסמא", "error");
        }
    };

    const handleRoleChange = async (id, newRole) => {
        try {
            const user = users.find(u => u.id === id);
            await axios.put(`${ctx.api_url}/users/${id}`, {
                email: user.email,
                password: "KEEP_CURRENT",
                type: newRole
            }, { headers });
            setUsers(users.map(u => u.id === id ? { ...u, type: newRole } : u));
            showMessage("תפקיד עודכן בהצלחה");
        } catch (err) {
            showMessage("שגיאה בעדכון תפקיד", "error");
        }
    };

    const handleAddUser = async () => {
        const errors = validate(newUser.email, newUser.password);
        if (Object.keys(errors).length > 0) {
            if (errors.email) showMessage(errors.email, "error");
            else if (errors.password) showMessage(errors.password, "error");
            return;
        }

        if (!newUser.email || !newUser.password) return;
        try {
            await axios.post(`${ctx.api_url}/register`, newUser, { headers });
            setShowNewUser(false);
            setNewUser({ email: "", password: "", role: "user" });
            fetchUsers();
            showMessage("משתמש נוסף בהצלחה");
        } catch (err) {
            showMessage("שגיאה בהוספת משתמש", "error");
        }
    };

    return protect && (
        <section className="users-editor">
            <div className="users-header">
                <h2>ניהול משתמשים</h2>
                <button className="btn-add" onClick={() => setShowNewUser(!showNewUser)}>
                    {showNewUser ? "ביטול" : "+ משתמש חדש"}
                </button>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>{message.text}</div>
            )}

            {showNewUser && (
                <div className="new-user-form">
                    <h3>הוסף משתמש חדש</h3>
                    <div className="form-row">
                        <input
                            type="email"
                            placeholder="אימייל"
                            value={newUser.email}
                            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        />
                        <input
                            type="password"
                            placeholder="סיסמא"
                            value={newUser.password}
                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                        />
                        <select
                            value={newUser.role}
                            onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                        >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                        </select>
                        <button className="btn-save" onClick={handleAddUser}>הוסף</button>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="loading">טוען...</p>
            ) : (
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>אימייל</th>
                            <th>סיסמא</th>
                            <th>תפקיד</th>
                            <th>פעולות</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.email}</td>
                                <td>
                                    {resetId === user.id ? (
                                        <div className="reset-row">
                                            <input
                                                type="password"
                                                placeholder="סיסמא חדשה"
                                                value={resetPassword}
                                                onChange={e => setResetPassword(e.target.value)}
                                            />
                                            <button className="btn-save" onClick={() => handleResetPassword(user.id)}>שמור</button>
                                            <button className="btn-cancel" onClick={() => { setResetId(null); setResetPassword(""); }}>ביטול</button>
                                        </div>
                                    ) : (
                                        <div className="password-row">
                                            <span>••••••••</span>
                                            <button className="btn-reset" onClick={() => setResetId(user.id)}>איפוס סיסמא</button>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <select
                                        value={user.type}
                                        onChange={e => handleRoleChange(user.id, e.target.value)}
                                        className={`role-select ${user.type}`}
                                    >
                                        <option value="user">user</option>
                                        <option value="admin">admin</option>
                                    </select>
                                </td>
                                <td>
                                    {deleteConfirm === user.id ? (
                                        <div className="confirm-row">
                                            <span>בטוח?</span>
                                            <button className="btn-delete" onClick={() => handleDelete(user.id)}>כן</button>
                                            <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>לא</button>
                                        </div>
                                    ) : user.id !== 1 && (
                                        <button className="btn-delete" onClick={() => setDeleteConfirm(user.id)}>מחק</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
};

export default UsersEditor;